import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/Button";

/* Fit2Lift brand tokens (from .cursor/rules/fit2lift-brand-tokens.mdc) */
const F2L = {
  deepSlate: "#0B1320",
  charcoal: "var(--color-charcoal)",
  surface2: "var(--color-surface-elevated)",
  navy: "#1D3F64",
  signalBlue: "#2E6DB4",
  highlightBlue: "#4A9FD5",
  steelGray: "var(--color-steel-gray)",
  midGray: "#6B7178",
  white: "#FFFFFF",
  borderSteel: "var(--color-border)",
  focusRing: "0 0 0 2px rgba(46, 109, 180, 0.25)",
} as const;

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) {
        setError(err.message);
        return;
      }
      // AuthContext will update via onAuthStateChange; App redirects when user is set.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen min-h-[100dvh] flex flex-col justify-center items-center text-white font-sans py-8 px-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      style={{ background: F2L.deepSlate }}
    >
      {/* Single card: logo + form, vertically centered */}
      <div
        className="w-full max-w-[28rem] shadow-xl border py-8 px-4 sm:px-10"
        style={{
          background: F2L.charcoal,
          borderColor: F2L.borderSteel,
          borderRadius: "var(--radius-lg)",
        }}
      >
        {/* Full logo inside container */}
        <div className="flex justify-center mb-8">
          <Link to="/" className="block w-full max-w-[1050px] sm:max-w-[1200px]">
            <img
              src="/F2Lfulllogowithhex.png"
              alt="Fit2Lift"
              className="w-full h-auto"
            />
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium uppercase tracking-wider"
                  style={{
                    color: F2L.steelGray,
                    fontFamily: "var(--font-display, 'Barlow', sans-serif)",
                    letterSpacing: "0.08em",
                  }}
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="block w-full px-3 py-2 border shadow-sm placeholder:text-[#6B7178] focus:outline-none text-base disabled:opacity-50"
                    style={{
                      background: F2L.surface2,
                      borderColor: F2L.borderSteel,
                      color: F2L.white,
                      minHeight: 48,
                      borderRadius: "var(--radius-md)",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = F2L.signalBlue;
                      e.target.style.boxShadow = F2L.focusRing;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = F2L.borderSteel;
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 min-h-[44px]">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium uppercase tracking-wider"
                    style={{
                      color: F2L.steelGray,
                      fontFamily: "var(--font-display, 'Barlow', sans-serif)",
                      letterSpacing: "0.08em",
                    }}
                  >
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium transition-colors hover:opacity-90"
                    style={{ color: F2L.highlightBlue }}
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="block w-full px-3 py-2 pr-12 border shadow-sm placeholder:text-[#6B7178] focus:outline-none text-base disabled:opacity-50"
                    style={{
                      background: F2L.surface2,
                      borderColor: F2L.borderSteel,
                      color: F2L.white,
                      minHeight: 48,
                      borderRadius: "var(--radius-md)",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = F2L.signalBlue;
                      e.target.style.boxShadow = F2L.focusRing;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = F2L.borderSteel;
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {!showPassword ? (
                      <svg
                        className="h-5 w-5 hover:opacity-80"
                        style={{ color: F2L.midGray }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 hover:opacity-80"
                        style={{ color: F2L.midGray }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div
                  className="rounded border p-4"
                  style={{
                    background: "rgba(155, 32, 32, 0.2)",
                    borderColor: "#9B2020",
                  }}
                  role="alert"
                >
                  <p className="text-sm font-medium text-red-200">{error}</p>
                </div>
              )}

              {/* Sign in — shared app primary button */}
              <div className="pt-2">
                <Button type="submit" loading={loading} className="w-full justify-center py-3">
                  Sign in
                </Button>
              </div>
            </form>

        {/* Need help / divider */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: F2L.borderSteel }} />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2" style={{ background: F2L.charcoal, color: F2L.midGray }}>
                Need help?
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs" style={{ color: F2L.midGray }}>
            By signing, in you agree to our{" "}
            <a href="#" className="transition-opacity hover:opacity-90" style={{ color: F2L.highlightBlue }}>
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="#" className="transition-opacity hover:opacity-90" style={{ color: F2L.highlightBlue }}>
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
